/**
 * Where a Team's tickets come from, as three choices (design notes, "What
 * Bailey sees"): From a filter (the common case, with the preview under it),
 * Only what I hand it (Add tickets, or assign the issue to yagni in Jira or Linear;
 * Bailey never picks), and Assigned to me (kept for the one-person Team).
 *
 * Controlled: the parent holds the mode, the filter text, the tracker and the
 * person, and decides what Save writes. Settings and Add a team both mount it.
 */
import type { ReactNode } from "react";
import type { BacklogPreviewResponse } from "@yagni/shared";
import { BacklogPreview, type BacklogSource } from "./BacklogPreview.js";

export type ReadsMode = "filter" | "hand" | "me";

export const ASSIGNED_TO_ME_JQL = "assignee = currentUser() AND statusCategory != Done";

export interface ReadsChoiceProps {
  deskId: string | null;
  /** The radio group's name; unique on the page. */
  name: string;
  mode: ReadsMode;
  onModeChange: (mode: ReadsMode) => void;
  filter: string;
  onFilterChange: (text: string) => void;
  tracker: BacklogSource;
  trackers: ReadonlyArray<BacklogSource>;
  onTrackerChange: (tracker: BacklogSource) => void;
  person: string | null;
  people: ReadonlyArray<{ userId: string; name: string }>;
  onPersonChange: (userId: string) => void;
  cap: number | null;
  disabled?: boolean;
  onAnswer?: (answer: BacklogPreviewResponse | null) => void;
  /** Add just KEY would leave unsaved edits behind; say so instead of leaving. */
  addJustBlocked?: boolean;
  /** Fold the From a filter box into one line once the tracker accepts the text. */
  foldOnAccept?: boolean;
}

const TRACKER_WORD: Record<BacklogSource, string> = { jira: "Jira", linear: "Linear" };

export function ReadsChoice({
  deskId,
  name,
  mode,
  onModeChange,
  filter,
  onFilterChange,
  tracker,
  trackers,
  onTrackerChange,
  person,
  people,
  onPersonChange,
  cap,
  disabled,
  onAnswer,
  addJustBlocked,
  foldOnAccept,
}: ReadsChoiceProps) {
  const option = (value: ReadsMode, title: string, desc: string, body: ReactNode) => (
    <>
      <label>
        <input
          type="radio"
          name={name}
          value={value}
          checked={mode === value}
          disabled={disabled}
          data-testid={`reads-mode-${value}`}
          onChange={() => onModeChange(value)}
        />
        <span>
          <b style={{ fontWeight: 600 }}>{title}</b>
          <span className="d"> · {desc}</span>
        </span>
      </label>
      {mode === value && (
        <div className="readsopt" data-reads={value}>
          {body}
        </div>
      )}
    </>
  );

  return (
    <div className="checklist readsopts" role="radiogroup" aria-label="Where tickets come from">
      {option(
        "filter",
        "From a filter",
        "Bailey picks from what it matches, up to the daily cap.",
        <>
          {trackers.length > 1 && (
            <select
              aria-label="Tracker"
              style={{ marginBottom: 6 }}
              value={tracker}
              disabled={disabled}
              onChange={(event) => onTrackerChange(event.target.value as BacklogSource)}
            >
              {trackers.map((entry) => (
                <option key={entry} value={entry}>
                  {TRACKER_WORD[entry]}
                </option>
              ))}
            </select>
          )}
          <BacklogPreview
            deskId={deskId}
            value={filter}
            onChange={disabled ? undefined : onFilterChange}
            source={tracker}
            cap={cap}
            onAnswer={onAnswer}
            addJustBlocked={addJustBlocked}
            foldOnAccept={disabled ? false : foldOnAccept}
          />
        </>,
      )}
      {option(
        "hand",
        "Only what I hand it",
        "Add tickets on the queue, or assign the issue to yagni in Jira or Linear. Bailey never picks on its own.",
        <div className="blst quiet" style={{ padding: 0 }}>
          Assigning an issue to <b style={{ color: "var(--text-primary)" }}>yagni</b> in Jira or
          Linear lands it in Needs you within a minute, with the assignment as the reason. Nothing
          else is read.
        </div>,
      )}
      {option(
        "me",
        "Assigned to me",
        "Everything open that Jira or Linear assigns to one person, as it changes.",
        <>
          <select
            aria-label="Person"
            style={{ marginBottom: 6 }}
            value={person ?? ""}
            disabled={disabled}
            onChange={(event) => onPersonChange(event.target.value)}
          >
            {person == null && <option value="">Choose a person</option>}
            {people.map((entry) => (
              <option key={entry.userId} value={entry.userId}>
                {entry.name}
              </option>
            ))}
          </select>
          <div className="blecho">
            <span className="label">What Bailey reads</span>
            <code>{ASSIGNED_TO_ME_JQL}</code>
          </div>
          <span className="fact">
            Jira answers this as the person above, so it is not checked here.
          </span>
        </>,
      )}
    </div>
  );
}
